import Card from "components/card";
import InputField from "components/fields/InputField";
import React, { useEffect, useState } from "react";
import {
  MdReceiptLong,
  MdTimer,
  MdPhone,
  MdCalendarToday,
  MdEventNote,
  MdPlayCircleOutline,
  MdCheckCircleOutline,
} from "react-icons/md";

const ClientStats = ({ client, saveClientSection }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [localData, setLocalData] = useState({
    currency: "",
    projectsCount: 0,
    lastContactedAt: "",
    nextFollowUpAt: "",
    dueDate: "",
    startDate: "",
    completedDate: "",
    estimatedValue: "",
    estimatedHours: "",
    actualHours: "",
  });

  useEffect(() => {
    if (!isEditing) {
      setLocalData({
        estimatedValue: client?.estimatedValue ?? client?.totalRevenue ?? client?.revenue ?? client?.estimatedAmount ?? "",
        currency: client?.currency ?? "INR",
        projectsCount: client?.projectsCount ?? client?.projects?.length ?? client?.workIds?.length ?? 0,
        lastContactedAt: client?.lastContactedAt || client?.lastContacted || client?.modifiedAt || client?.updatedAt || "",
        nextFollowUpAt: client?.nextFollowUpAt || "",
        dueDate: client?.dueDate || "",
        startDate: client?.startDate || "",
        completedDate: client?.completedDate || "",
        estimatedHours: client?.estimatedHours || "",
        actualHours: client?.actualHours || "",
      });
    }
  }, [client, isEditing]);

  const handleFieldChange = (key, value) => {
    setLocalData((prev) => ({ ...prev, [key]: value }));
  };

  const formatDate = (value) => {
    if (!value) return "Not available";
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime())
      ? String(value)
      : parsed.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await saveClientSection(
        {
          estimatedValue: localData.estimatedValue,
          currency: localData.currency,
          lastContactedAt: localData.lastContactedAt,
          nextFollowUpAt: localData.nextFollowUpAt,
          estimatedHours: localData.estimatedHours,
          actualHours: localData.actualHours,
          dueDate: localData.dueDate,
          startDate: localData.startDate,
          completedDate: localData.completedDate,
        },
        "Client Stats"
      );
      setIsEditing(false);
    } catch (error) {
      // Error is handled by saveClientSection
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Card extra="w-full rounded-[24px] border border-gray-200 p-6 shadow-sm dark:border-white/10 dark:bg-navy-700">
      <div className="mb-5 flex items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-bold text-navy-900 dark:text-white">Financial & Engagement</h2>
          <p className="text-sm text-gray-500 dark:text-gray-400">Key revenue and activity metrics for this client.</p>
        </div>
        <div className="flex items-center gap-2">
          {isEditing ? (
            <>
              <button
                onClick={() => setIsEditing(false)}
                disabled={isSaving}
                className="inline-flex items-center gap-2 rounded-2xl bg-slate-500 px-4 py-2 text-sm font-semibold text-white transition hover:bg-slate-600 disabled:opacity-60"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={isSaving}
                className="inline-flex items-center gap-2 rounded-2xl bg-emerald-500 px-4 py-2 text-sm font-semibold text-white transition hover:bg-emerald-600 disabled:opacity-60"
              >
                {isSaving ? "Saving..." : "Save"}
              </button>
            </>
          ) : (
            <button
              onClick={() => setIsEditing(true)}
              className="inline-flex items-center gap-2 rounded-2xl bg-brand-500 px-4 py-2 text-sm font-semibold text-white transition hover:bg-brand-600"
            >
              Edit
            </button>
          )}
        </div>
      </div>

      <div className="grid gap-4 grid-cols-1 md:grid-cols-2 lg:grid-cols-4">
        <div className="rounded-2xl border border-gray-200 bg-slate-50 p-4 text-center shadow-sm dark:border-white/10 dark:bg-navy-800">
          <div className="mx-auto mb-2 grid h-10 w-10 place-items-center rounded-3xl bg-slate-100 text-slate-700 dark:bg-slate-900/20 dark:text-slate-300">
            <span className="text-lg font-semibold">₹</span>
          </div>
          <p className="text-xs uppercase tracking-[0.01em] text-gray-400">Estimated Amount</p>
          {isEditing ? (
            <InputField
              id="client-estimatedValue"
              label=""
              type="number"
              placeholder="Estimated Amount"
              value={String(localData.estimatedValue)}
              onChange={(e) => handleFieldChange("estimatedValue", e.target.value)}
              extra="mt-3 w-full"
            />
          ) : (
            <p className="mt-3 text-2xl font-semibold text-navy-900 dark:text-white">{localData.currency} {Number(localData.estimatedValue || 0).toLocaleString()}</p>
          )}
        </div>

        <div className="rounded-2xl border border-gray-200 bg-slate-50 p-4 text-center shadow-sm dark:border-white/10 dark:bg-navy-800">
          <div className="mx-auto mb-2 grid h-10 w-10 place-items-center rounded-3xl bg-slate-100 text-slate-700 dark:bg-slate-900/20 dark:text-slate-300">
            <MdTimer className="h-5 w-5" />
          </div>
          <p className="text-xs uppercase tracking-[0.01em] text-gray-400">Estimated Hours</p>
          {isEditing ? (
            <InputField
              id="client-estimatedHours"
              label=""
              type="number"
              placeholder="Estimated Hours"
              value={localData.estimatedHours}
              onChange={(e) => handleFieldChange("estimatedHours", e.target.value)}
              extra="mt-3"
            />
          ) : (
            <p className="mt-3 text-lg font-semibold text-navy-900 dark:text-white">{localData.estimatedHours || "-"}</p>
          )}
        </div>

        <div className="rounded-2xl border border-gray-200 bg-slate-50 p-4 text-center shadow-sm dark:border-white/10 dark:bg-navy-800">
          <div className="mx-auto mb-2 grid h-10 w-10 place-items-center rounded-3xl bg-slate-100 text-slate-700 dark:bg-slate-900/20 dark:text-slate-300">
            <MdTimer className="h-5 w-5" />
          </div>
          <p className="text-xs uppercase tracking-[0.01em] text-gray-400">Actual Hours</p>
          {isEditing ? (
            <InputField
              id="client-actualHours"
              label=""
              type="number"
              placeholder="Actual Hours"
              value={localData.actualHours}
              onChange={(e) => handleFieldChange("actualHours", e.target.value)}
              extra="mt-3"
            />
          ) : (
            <p className="mt-3 text-lg font-semibold text-navy-900 dark:text-white">{localData.actualHours || "-"}</p>
          )}
        </div>

        <div className="rounded-3xl border border-gray-200 bg-slate-50 p-4 text-center shadow-sm dark:border-white/10 dark:bg-navy-800">
          <div className="mx-auto mb-3 grid h-12 w-12 place-items-center rounded-3xl bg-slate-100 text-slate-700 dark:bg-slate-900/20 dark:text-slate-300">
            <MdPhone className="h-6 w-6" />
          </div>
          <p className="text-xs uppercase tracking-[0.01em] text-gray-400">Last Contacted</p>
          {isEditing ? (
            <InputField
              id="client-lastContactedAt"
              label=""
              type="date"
              placeholder="Last contacted"
              value={localData.lastContactedAt ? new Date(localData.lastContactedAt).toISOString().slice(0, 10) : ""}
              onChange={(e) => handleFieldChange("lastContactedAt", e.target.value)}
              extra="mt-3"
            />
          ) : (
            <p className="mt-3 text-lg font-semibold text-navy-900 dark:text-white">{formatDate(localData.lastContactedAt)}</p>
          )}
        </div>

        <div className="rounded-3xl border border-gray-200 bg-slate-50 p-4 text-center shadow-sm dark:border-white/10 dark:bg-navy-800">
          <div className="mx-auto mb-3 grid h-12 w-12 place-items-center rounded-3xl bg-slate-100 text-slate-700 dark:bg-slate-900/20 dark:text-slate-300">
            <MdCalendarToday className="h-6 w-6" />
          </div>
          <p className="text-xs uppercase tracking-[0.01em] text-gray-400">Next Follow-up</p>
          {isEditing ? (
            <InputField
              id="client-nextFollowUpAt"
              label=""
              type="date"
              placeholder="Next follow-up"
              value={localData.nextFollowUpAt ? new Date(localData.nextFollowUpAt).toISOString().slice(0, 10) : ""}
              onChange={(e) => handleFieldChange("nextFollowUpAt", e.target.value)}
              extra="mt-3"
            />
          ) : (
            <p className="mt-3 text-lg font-semibold text-navy-900 dark:text-white">{formatDate(localData.nextFollowUpAt)}</p>
          )}
        </div>

        <div className="rounded-3xl border border-gray-200 bg-slate-50 p-4 text-center shadow-sm dark:border-white/10 dark:bg-navy-800">
          <div className="mx-auto mb-3 grid h-12 w-12 place-items-center rounded-3xl bg-slate-100 text-slate-700 dark:bg-slate-900/20 dark:text-slate-300">
            <MdEventNote className="h-6 w-6" />
          </div>
          <p className="text-xs uppercase tracking-[0.01em] text-gray-400">Due Date</p>
          {isEditing ? (
            <InputField
              id="client-dueDate"
              label=""
              type="date"
              placeholder="Due Date"
              value={localData.dueDate ? new Date(localData.dueDate).toISOString().slice(0, 10) : ""}
              onChange={(e) => handleFieldChange("dueDate", e.target.value)}
              extra="mt-3"
            />
          ) : (
            <p className="mt-3 text-lg font-semibold text-navy-900 dark:text-white">{formatDate(localData.dueDate)}</p>
          )}
        </div>

        <div className="rounded-3xl border border-gray-200 bg-slate-50 p-4 text-center shadow-sm dark:border-white/10 dark:bg-navy-800">
          <div className="mx-auto mb-3 grid h-12 w-12 place-items-center rounded-3xl bg-slate-100 text-slate-700 dark:bg-slate-900/20 dark:text-slate-300">
            <MdPlayCircleOutline className="h-6 w-6" />
          </div>
          <p className="text-xs uppercase tracking-[0.01em] text-gray-400">Start Date</p>
          {isEditing ? (
            <InputField
              id="client-startDate"
              label=""
              type="date"
              placeholder="Start Date"
              value={localData.startDate ? new Date(localData.startDate).toISOString().slice(0, 10) : ""}
              onChange={(e) => handleFieldChange("startDate", e.target.value)}
              extra="mt-3"
            />
          ) : (
            <p className="mt-3 text-lg font-semibold text-navy-900 dark:text-white">{formatDate(localData.startDate)}</p>
          )}
        </div>

        <div className="rounded-3xl border border-gray-200 bg-slate-50 p-4 text-center shadow-sm dark:border-white/10 dark:bg-navy-800">
          <div className="mx-auto mb-3 grid h-12 w-12 place-items-center rounded-3xl bg-slate-100 text-slate-700 dark:bg-slate-900/20 dark:text-slate-300">
            <MdCheckCircleOutline className="h-6 w-6" />
          </div>
          <p className="text-xs uppercase tracking-[0.01em] text-gray-400">Completed Date</p>
          {isEditing ? (
            <InputField
              id="client-completedDate"
              label=""
              type="date"
              placeholder="Completed Date"
              value={localData.completedDate ? new Date(localData.completedDate).toISOString().slice(0, 10) : ""}
              onChange={(e) => handleFieldChange("completedDate", e.target.value)}
              extra="mt-3"
            />
          ) : (
            <p className="mt-3 text-lg font-semibold text-navy-900 dark:text-white">{formatDate(localData.completedDate)}</p>
          )}
        </div>
      </div>
    </Card>
  );
};

export default ClientStats;
